import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { getCustomerInvoices } from 'src/api/apiCaller';
import { useAppDispatch } from 'src/hooks';
import { Invoice } from 'src/types/invoice';
import { showSnackbar } from 'src/utils/snackbar';
import InvoicesTableLoading from './InvoicesTableLoading';
import InvoicesTableHeaderRow from './InvoicesTableHeaderRow';
import InvoicesTableBody from './InvoicesTableBody';
import {
    InvoicesTableScrollContainer,
    InvoicesTableSurface,
    InvoicesTableHeadRoot,
    InvoicesTablePaginationBar
} from './styled';

interface Props {
    projectId: string;
}

const InvoicesTable: FunctionComponent<Props> = ({ projectId }) => {
    const dispatch = useAppDispatch();
    const invoicesRequestIdRef = useRef(0);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchInvoices = useCallback(
        async (pageNum: number, rowsPerPage: number) => {
            invoicesRequestIdRef.current += 1;
            const requestId = invoicesRequestIdRef.current;
            setLoading(true);
            try {
                const response = await getCustomerInvoices(
                    projectId,
                    pageNum + 1,
                    rowsPerPage
                );
                if (requestId !== invoicesRequestIdRef.current) {
                    return;
                }
                const data = response.data || response;
                setInvoices(data.invoices || []);
                setTotal(data.total || 0);
            } catch (error) {
                if (requestId !== invoicesRequestIdRef.current) {
                    return;
                }
                console.error('Failed to fetch invoices:', error);
                showSnackbar(
                    dispatch,
                    'error',
                    'Failed to load invoices. Please try again.'
                );
                setInvoices([]);
                setTotal(0);
            } finally {
                if (requestId === invoicesRequestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [dispatch, projectId]
    );

    useEffect(() => {
        fetchInvoices(page, perPage);
    }, [fetchInvoices, page, perPage]);

    useEffect(() => {
        return () => {
            invoicesRequestIdRef.current += 1;
        };
    }, []);

    const handleChangePage = useCallback(
        (
            _event: React.MouseEvent<HTMLButtonElement> | null,
            newPage: number
        ) => {
            setPage(newPage);
        },
        []
    );

    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setPerPage(parseInt(event.target.value, 10));
            setPage(0);
        },
        []
    );

    if (loading && invoices.length === 0) {
        return <InvoicesTableLoading />;
    }

    return (
        <Box>
            <InvoicesTableScrollContainer component={Paper}>
                <InvoicesTableSurface aria-label="invoices table">
                    <InvoicesTableHeadRoot>
                        <InvoicesTableHeaderRow />
                    </InvoicesTableHeadRoot>
                    <InvoicesTableBody invoices={invoices} loading={loading} />
                </InvoicesTableSurface>
            </InvoicesTableScrollContainer>
            <InvoicesTablePaginationBar
                component="div"
                count={total}
                page={page}
                rowsPerPageOptions={[10, 20, 50, 100]}
                onPageChange={handleChangePage}
                rowsPerPage={perPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default InvoicesTable;
